/**
 * Renders the toast stack. Sits above the column but below dialogs — a confirmation must never
 * be obscured by a notification about the thing it is confirming.
 */

import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../ui/cn.js';
import { dismissToast, getToasts, subscribeToToasts, type ToastTone } from './toastStore.js';

const TONES: Record<ToastTone, string> = {
  success: 'bg-success-subtle text-success-subtle-text border-success-border',
  danger: 'bg-danger-subtle text-danger-subtle-text border-danger-border',
  warning: 'bg-warning-subtle text-warning-subtle-text border-warning-border',
  info: 'bg-info-subtle text-info-subtle-text border-info-border',
};

export function ToastHost() {
  const toasts = useSyncExternalStore(subscribeToToasts, getToasts, getToasts);

  if (toasts.length === 0) return null;

  return createPortal(
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-toast flex flex-col items-center gap-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]"
      data-print="hide"
    >
      {toasts.map((item) => (
        <div
          key={item.id}
          // `status`, not `alert`: an alert interrupts a screen reader mid-sentence, and a
          // salesperson using one is usually mid-sentence with a customer.
          role="status"
          aria-live="polite"
          className={cn(
            'pointer-events-auto flex w-full max-w-column items-center gap-3 rounded-card border px-4 py-3 shadow-lg',
            'motion-safe:animate-[toast-in_190ms_var(--pos-ease-spring)]',
            TONES[item.tone],
          )}
        >
          <p className="flex-1 text-sm font-medium">{item.message}</p>

          {item.action !== undefined && (
            <button
              type="button"
              onClick={() => {
                item.action?.run();
                dismissToast(item.id);
              }}
              className="shrink-0 rounded-md px-2 py-1 text-sm font-bold underline underline-offset-2"
            >
              {item.action.label}
            </button>
          )}

          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => {
              dismissToast(item.id);
            }}
            className="shrink-0 rounded-md p-1 opacity-60 hover:opacity-100"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      ))}

      <style>{`@keyframes toast-in { from { transform: translateY(12px); opacity: 0 } to { transform: none; opacity: 1 } }`}</style>
    </div>,
    document.body,
  );
}
